import { Muxer, ArrayBufferTarget } from 'mp4-muxer';
import * as ebml from 'ts-ebml';
import { readAsArrayBuffer } from 'components/Utils';

class SampleRecorder {
  constructor({ stream, duration = 5500, videoBitsPerSecond, videoOpts }) {
    this.stream = stream;
    this.duration = duration;
    this.videoBitsPerSecond = videoBitsPerSecond;
    this.videoOpts = videoOpts;
  }

  recordWebm = () => {
    return new Promise((resolve, reject) => {
      const audioBitsPerSecond = 32000;
      const recorder = new MediaRecorder(this.stream, { mimeType: 'video/webm', videoBitsPerSecond: this.videoBitsPerSecond, audioBitsPerSecond });
      recorder.ondataavailable = async (e) => resolve(e.data);
      recorder.start();
      setTimeout(() => {
        recorder.stop();
      }, this.duration);
    })
  }

  getMetaDataFromChunk = async (chunk) => {
    const decoder = new ebml.Decoder();
    const reader = new ebml.Reader();
    const buffer = await readAsArrayBuffer(chunk);
    const elms = decoder.decode(buffer);
    elms.forEach((elm) => { reader.read(elm); });
    // const refinedMetadataBuf = ebml.tools.makeMetadataSeekable(reader.metadatas, reader.duration, reader.cues);
    console.log("[SAMPLE RECORDER]", "got metadata", reader.metadataSize, reader.metadatas, elms);
    const result = buffer.slice(0, reader.metadataSize);
    return result;
  }

  addMetadataToChunk = (chunk, metadata) => {
    return new Blob([metadata, chunk], { type: chunk.type });
  }

  recordMp4 = async () => {
    const stream = this.stream
    const hasVideo = stream.getVideoTracks().length > 0;

    const audioTrack = stream.getAudioTracks()[0];
    const videoTrack = stream.getVideoTracks()[0];
    const audioSampleRate = audioTrack?.getSettings().sampleRate || 48000;
    const channelCount = audioTrack?.getSettings().channelCount || 1;
    console.log("[SAMPLE RECORDER]", "audio tracks:", stream.getAudioTracks().length, "sample rate:", audioTrack.getSettings().sampleRate, "channel count:", audioTrack.getSettings().channelCount);
    let videoTrackWidth = videoTrack?.getSettings().width;
    let videoTrackHeight = videoTrack?.getSettings().height;
    const frameRate = videoTrack?.getSettings().frameRate || this.videoOpts?.frameRate?.ideal || 25;
    console.log("[SAMPLE RECORDER]", "video tracks:", stream.getVideoTracks().length, "video frame rate:", frameRate);
    let muxer = new Muxer({
      target: new ArrayBufferTarget(),
      video: hasVideo ? {
        codec: 'avc',
        width: videoTrackWidth,
        height: videoTrackHeight
      } : undefined,
      audio: {
        codec: 'aac',
        sampleRate: audioSampleRate,
        numberOfChannels: channelCount,
      },
      fastStart: 'in-memory',
      firstTimestampBehavior: 'offset'
    });

    const audioBitsPerSecond = 96000;

    let videoEncoder;
    let videoError = null;
    if (hasVideo) {
      //eslint-disable-next-line
      videoEncoder = new VideoEncoder({
        output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
        error: e => {
          console.error("[SAMPLE RECORDER]", "video encoder error", e);
          videoError = e;
        }
      });
      videoEncoder.configure({
        codec: 'avc1.42003e',
        width: videoTrackWidth,
        height: videoTrackHeight,
        bitrate: this.videoBitsPerSecond,
        framerate: frameRate,
      });
    }
    let audioError = null;
    //eslint-disable-next-line
    let audioEncoder = new AudioEncoder({
      output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
      error: e => {
        console.error("[SAMPLE RECORDER]", "audio encoder error", e);
        audioError = e;
      }
    });
    audioEncoder.configure({
      codec: 'mp4a.40.2',
      numberOfChannels: channelCount,
      sampleRate: audioSampleRate,
      bitrate: audioBitsPerSecond
    });

    let recording = false;

    // let frameCount = 0;
    // const keyframeInterval = 300000;
    let videoTrackProcessor;
    let videoConsumer;
    let videoFrameCount = 0;
    let lastVideoFrame = null;
    // let recordingStartTime = Date.now();
    if (hasVideo) {
      // eslint-disable-next-line
      videoTrackProcessor = new MediaStreamTrackProcessor({ track: videoTrack });
      // let firstVideoFrameTimestamp = 0;
      // let firstVideoFrameDate = 0;
      videoConsumer = new WritableStream({
        write(videoFrame) {
          if (!recording || videoEncoder.state !== "configured") {
            videoFrame.close();
            return;
          }
          // console.log("[SAMPLE RECORDER]", "video frame", videoFrameCount++, videoEncoder.encodeQueueSize, "time diff", Math.round((videoFrame.timestamp - firstVideoFrameTimestamp) / 1000), Date.now() - firstVideoFrameDate);
          // const isKeyframe = frameCount % keyframeInterval === 0;
          // if (videoFrameCount === 1) {
          //   firstVideoFrameTimestamp = videoFrame.timestamp;
          //   firstVideoFrameDate = Date.now();
          // }
          videoFrameCount++;
          lastVideoFrame = videoFrame;
          const isKeyframe = videoFrameCount % 25 === 0;
          videoEncoder.encode(videoFrame, { keyFrame: isKeyframe });
          videoFrame.close();

          // frameCount++;
        }
      });
    }
    let audioFrameCount = 0;
    let lastAudioFrame = null;
    // let firstAudioFrameTimestamp = 0;
    // let firstAudioFrameDate = 0;
    // eslint-disable-next-line
    let audioTrackProcessor = new MediaStreamTrackProcessor({ track: audioTrack });
    let audioConsumer = new WritableStream({
      write(audioData) {
        if (!recording || audioEncoder.state !== "configured") {
          audioData.close();
          return;
        }
        // console.log("[SAMPLE RECORDER]", "audio frame", audioFrameCount++, audioEncoder.encodeQueueSize, "time diff", Math.round((audioData.timestamp - firstAudioFrameTimestamp) / 1000), Date.now() - firstAudioFrameDate);
        audioFrameCount++;
        // if (audioFrameCount === 1) {
        //   firstAudioFrameDate = Date.now();
        //   firstAudioFrameTimestamp = audioData.timestamp;
        // }
        lastAudioFrame = audioData;
        audioEncoder.encode(audioData);
        audioData.close();
      }
    });
    if (hasVideo)
      videoTrackProcessor.readable.pipeTo(videoConsumer);
    audioTrackProcessor.readable.pipeTo(audioConsumer);

    let videoEncoderQueueSize = undefined;
    let audioEncoderQueueSize = undefined;
    // recordingStartTime = Date.now();
    recording = true;
    const recordSample = () => {
      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            recording = false;
            console.log("[SAMPLE RECORDER]", "stopping recording", (lastVideoFrame?.timestamp ?? 0) - lastAudioFrame.timestamp, { audioFrameCount, videoFrameCount, lastAudioFrame, lastVideoFrame });
            let promises = [];
            if (videoEncoder) {
              if (videoEncoder.state === "configured") {
                console.log("[SAMPLE RECORDER]", "flushing video encoder", videoEncoder.encodeQueueSize, videoEncoder);
                // await videoEncoder.flush();
                videoEncoderQueueSize = videoEncoder.encodeQueueSize;
                promises.push(videoEncoder.flush());
              } else {
                console.error("[SAMPLE RECORDER]", "video encoder not configured, state:", videoEncoder.state);
                throw new Error(videoError ? "Video error, " + videoError.message : "video encoder not configured");
              }
            }
            if (audioEncoder.state === "configured") {
              console.log("[SAMPLE RECORDER]", "flushing audio encoder", audioEncoder.encodeQueueSize, audioEncoder);
              audioEncoderQueueSize = audioEncoder.encodeQueueSize;
              // await audioEncoder?.flush();
              promises.push(audioEncoder.flush());
            } else {
              console.error("[SAMPLE RECORDER]", "audio encoder not configured, state:", audioEncoder.state);
              throw new Error(audioError ? "Audio error, " + audioError.message : "audio encoder not configured");
            }
            await Promise.all(promises);
            muxer.finalize();
            let { buffer } = muxer.target;
            console.log({ buffer });
            const blob = new Blob([buffer], { type: 'video/mp4' });
            console.log("[SAMPLE RECORDER]", "sample recorded", blob);
            resolve(blob);
          } catch (ex) {
            console.error("[SAMPLE RECORDER]", "sample recording error", ex);
            reject(ex);
          }
        }, this.duration);
      });
    }

    const blob = await recordSample();
    const sampleMetadata = { audioEncoderQueueSize, videoEncoderQueueSize, lastAudioFrame: lastAudioFrame.timestamp, lastVideoFrame: lastVideoFrame?.timestamp, audioFrameCount, videoFrameCount, audioSampleRate, channelCount, frameRate, timestampDiff: lastVideoFrame ? lastVideoFrame.timestamp - lastAudioFrame.timestamp : undefined };
    return { blob, sampleMetadata };
  }
}

export default SampleRecorder;